import t from "@/lang/lang";
import { fMoney } from "@/routes/processes/_components/ui/number-input";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Value } from "@/shared/components/domain/qpm/inputs/number-input";
import { CalculationServiceEntryType } from "@/services/backend/qpm/calculations/types/calculation-enum";
import { useCalculationData } from "@/services/backend/qpm/calculations/hooks/calculation-context";
import { MaintenanceOverviewComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/total/_components/maintenance-overview-component";
import { FlyExterminatorMaintenanceOverviewComponent } from "@/routes/processes/[processId]/qpm/calculations/[calculationId]/total/_components/fly-exterminator-maintenance-overview-component";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/shared/components/ui/table";

export function TotalOverviewRoute() {
  const { calculation, isLoading, error } = useCalculationData();

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return (
      <Card className="text-muted-foreground animate-pulse p-6">
        {t("Lade Daten...")}
      </Card>
    );
  }

  return (
    calculation && (
      <Card>
        <CardHeader>
          <CardTitle>{t("Gesamtübersicht")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4">
            <TotalCostMaterialComponent
              totalCostCheckPointMaterial={
                calculation.totalCostCheckPointMaterial
              }
              totalCostFlyExterminatorMaterial={
                calculation.totalCostFlyExterminatorMaterial
              }
              totalCostALoRaMaterial={calculation.totalCostALoRaMaterial}
            />
            <InstallationCostsComponent
              totalCost={calculation.installationService?.totalCost}
            />
            <Card>
              <CardHeader>
                <CardTitle>{t("Wartungskosten")}</CardTitle>
              </CardHeader>
              <CardContent>
                <MaintenanceOverviewComponent
                  results={calculation.maintenanceService.results}
                />
              </CardContent>
            </Card>
            <FlyExterminatorMaintenanceOverviewComponent
              flyExterminatorMaintenance={
                calculation.flyExterminatorMaintenance
              }
            />
            <RentingOverviewComponent
              rentingDuration={calculation.rentingResult.rentingDuration}
              rentPerMonthWithMaintenance={
                calculation.rentingResult.rentPerMonthWithMaintenance
              }
              aLoRaMaintenanceCost={
                calculation.maintenanceService.totalCost?.[
                  CalculationServiceEntryType.ALoRa
                ] || 0
              }
            />
          </div>
        </CardContent>
      </Card>
    )
  );
}

type TotalCostMaterialComponentProps = {
  totalCostCheckPointMaterial: number;
  totalCostFlyExterminatorMaterial: number;
  totalCostALoRaMaterial: number;
};

function TotalCostMaterialComponent({
  totalCostCheckPointMaterial,
  totalCostFlyExterminatorMaterial,
  totalCostALoRaMaterial,
}: TotalCostMaterialComponentProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Materialkosten")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Anschaffung Monitore QPM")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(totalCostCheckPointMaterial)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Anschaffung FIV")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(totalCostFlyExterminatorMaterial)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Anschaffung ALoRa")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(totalCostALoRaMaterial)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

type InstallationCostsComponentProps = {
  totalCost?: Record<CalculationServiceEntryType, number>;
};

function InstallationCostsComponent({
  totalCost,
}: InstallationCostsComponentProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Einrichtungskosten")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Einrichtung QPM")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(
                  totalCost?.[CalculationServiceEntryType.CheckPoint] || 0,
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Einrichtung QPM SbD")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(
                  totalCost?.[CalculationServiceEntryType.CheckPointSCB] || 0,
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Einrichtung Visuelle Prüfpunkte")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(
                  totalCost?.[CalculationServiceEntryType.CheckPointVisual] ||
                    0,
                )}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Einrichtung ALoRa")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(totalCost?.[CalculationServiceEntryType.ALoRa] || 0)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Preis Einrichtung FIV")}
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(totalCost?.[CalculationServiceEntryType.FIV] || 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

type RentingOverviewComponentProps = {
  rentingDuration: number;
  rentPerMonthWithMaintenance: number;
  aLoRaMaintenanceCost: number;
};

function RentingOverviewComponent({
  rentingDuration,
  rentPerMonthWithMaintenance,
  aLoRaMaintenanceCost,
}: RentingOverviewComponentProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("Miete")}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableBody>
            <TableRow>
              <TableCell className="font-semibold">
                {t("Miete pro Monat ALoRa")}
              </TableCell>
              <TableCell>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground">{t("Dauer")}</span>
                  <span className="text-base font-semibold">
                    <Value value={rentingDuration} fractions={0} />{" "}
                    {t("Monate")}
                  </span>
                </div>
              </TableCell>
              <TableCell className="text-end text-base font-semibold">
                {fMoney(rentPerMonthWithMaintenance)}
              </TableCell>
            </TableRow>
            <TableRow>
              <TableCell className="text-muted-foreground">
                {t("Wartungskosten ALoRa")}
              </TableCell>
              <TableCell />
              <TableCell className="text-end text-base font-semibold">
                {fMoney(aLoRaMaintenanceCost)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
